import { ItemRenderProps, TreeView } from "@progress/kendo-react-treeview";
import { DimensionItem, DimensionTreeItem, FormTreeSelection } from "./TreeAmountSelector";
import React, { useEffect, useState } from "react";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { getTheme } from "@fluentui/react";
import {
  mapDimensionToDimensionItem,
  mapNodeToDataItem,
  searchDimensions,
  searchDimensionsWithHierarchy,
} from "../../../api/object/Dimensions";
import { DimensionDetail } from "../../../api/models/dimension-detail";

export const NoResultScreen = ({ isFormTree }: { isFormTree?: boolean }) => {
  return (
    <div
      style={{
        height: "calc(40vh - 1rem)",
        objectFit: "contain",
        position: "relative",
        bottom: 0,
        backgroundColor: isFormTree ? "white" : "var(--color-lightblue)",
      }}
    >
      <img
        src={"/assets/NothingSelected.svg"}
        alt="nothing selected"
        style={{
          minWidth: "120px",
          width: "calc(100vw - 4rem)",
          height: "30vh",
          objectFit: "contain",
          position: "relative",
          top: "45%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      />
      <div style={{ textAlign: "center", color: "var(--color-blue)", paddingTop: "10px" }}>
        {formatMessage("cof.base.noresultfound")}
      </div>
    </div>
  );
};

export const BufferErrorScreen = ({ isFormTree }: { isFormTree?: boolean }) => {
  return (
    <div
      style={{
        height: "calc(43vh - 1rem)",
        objectFit: "contain",
        position: "relative",
        bottom: 0,
        backgroundColor: isFormTree ? "white" : "var(--color-lightblue)",
      }}
    >
      <img
        src={"/assets/manyResults.svg"}
        alt="nothing selected"
        style={{
          minWidth: "120px",
          width: "calc(100vw - 4rem)",
          height: "30vh",
          objectFit: "contain",
          position: "relative",
          top: "45%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      />
      <div style={{ textAlign: "center", color: "var(--color-blue)", paddingTop: "10px" }}>
        {formatMessage("cof.base.toomanyresults")}
      </div>
    </div>
  );
};
const SearchTreeViewItem =
  ({
    onCheckChange,
  }: {
    onCheckChange: (event: { item: DimensionSearchTreeItem; itemHierarchicalIndex: string }) => void;
  }) =>
  // eslint-disable-next-line react/display-name
  (props: ItemRenderProps) => {
    return (
      <div style={{ display: "flex", alignItems: "center", columnGap: "1rem" }}>
        <input
          id={props.item?.desc ?? props.item.desc}
          type={"checkbox"}
          className={"k-checkbox k-rounded-md k-checkbox-md"}
          style={{ backgroundColor: props.item.selectable ? "none" : "var(--color-disabled-bg)" }}
          disabled={!props.item.selectable}
          checked={props.item.checked}
          onClick={() =>
            onCheckChange({
              item: props.item,
              itemHierarchicalIndex: props.itemHierarchicalIndex,
            })
          }
          readOnly
          key={"0"}
        />
        <span>{props.item.code?.length ? props.item.code + " - " + props.item.desc : props.item.desc}</span>
        <span className="searchItemPath">{props.item.descPath.join("/")}</span>
      </div>
    );
  };

type DimensionSearchTreeItem = Omit<DimensionTreeItem, "children"> & {
  descPath: string[];
  oidPath: string[];
};

function getLastNodes(nodes: DimensionTreeItem[], rootOid: string): DimensionSearchTreeItem[] {
  return nodes.flatMap((node) => findLastNodes(node, [], [], rootOid));
}

function findLastNodes(
  node: DimensionTreeItem,
  descPath: string[],
  oidPath: string[],
  rootOid: string
): DimensionSearchTreeItem[] {
  const newOidPath = node.oid === rootOid ? [] : [...oidPath, node.oid];

  if (!node.children || node.children.length === 0) {
    return [{ ...node, oidPath: newOidPath, descPath }]; // Se il nodo non ha figli, è l'ultimo nodo
  }

  // Se il nodo ha figli, continua a cercare l'ultimo nodo nei figli
  const newDescPath = node.oid === rootOid ? [] : [...descPath, node.desc];
  return node.children.flatMap((childNode) => findLastNodes(childNode, newDescPath, newOidPath, rootOid));
}

function retrieveSearchCheckedData(roots: DimensionSearchTreeItem[], oidPath: string[], hierarchyOid: string) {
  return roots.map((r) => {
    const valueOidPath = hierarchyOid ? [hierarchyOid, ...oidPath] : oidPath;
    const rootOidPath = r.oidPath;
    if (rootOidPath.toString() === valueOidPath.toString()) return { ...r, checked: true };
    return r;
  });
}

const retrieveAllSearchedCheck = (roots: DimensionSearchTreeItem[], dimensions: DimensionDetail[] | undefined) => {
  let newRoots = roots;
  (dimensions ?? []).forEach((dimension) => {
    dimension.values.forEach(
      (value) => (newRoots = retrieveSearchCheckedData(newRoots, value.oidPath, dimension.hierarchy?.oid))
    );
  });
  return newRoots;
};

const uncheckAllSearched = (roots: DimensionSearchTreeItem[]) => roots.map((r) => ({ ...r, checked: false }));

export const SearchQueryAndResults = ({
  searchText,
  onChangeFromTree,
  currentDimensions,
  currentHierarchy,
  dbId,
  rootOid,
  hierarchical,
}: {
  searchText: string;
  onChangeFromTree: (formSelection: FormTreeSelection, overrideHierarchyOid?: string) => void;
  currentDimensions: DimensionDetail[];
  currentHierarchy: DimensionItem | undefined;
  dbId: string;
  rootOid: string;
  hierarchical: boolean;
}) => {
  const theme = getTheme();
  const [bufferError, setBufferError] = useState<boolean>(false);
  const [resultDimensions, setResultDimensions] = useState<DimensionSearchTreeItem[]>([]);

  const onSearchStringChange = async (searchString: string, currentHierarchy: DimensionItem | undefined) => {
    let foundDimensions = [];
    try {
      if (searchString.length >= 3) {
        if (currentHierarchy?.oid) {
          foundDimensions = await searchDimensionsWithHierarchy(dbId, rootOid, currentHierarchy?.oid, searchString);
        } else {
          foundDimensions = await searchDimensions(dbId, rootOid, searchString);
        }
      }
    } catch (err) {
      if (err.response && err.response.status === 413) {
        setBufferError(true);
      }
    }

    const newRoots = foundDimensions.map((i) => mapNodeToDataItem(mapDimensionToDimensionItem(i)));
    foundDimensions = retrieveAllSearchedCheck(getLastNodes(newRoots, rootOid), currentDimensions);
    setResultDimensions(foundDimensions);
  };

  useEffect(() => {
    onSearchStringChange(searchText, currentHierarchy);
    setBufferError(false);
  }, [searchText, currentHierarchy]);

  useEffect(() => {
    if (resultDimensions?.length) {
      const checked = retrieveAllSearchedCheck(uncheckAllSearched(resultDimensions), currentDimensions);
      setResultDimensions(checked);
    }
  }, [currentDimensions]);

  const onCheckChangeHierarchical = (event: { item: DimensionSearchTreeItem; itemHierarchicalIndex: string }) =>
    onChangeFromTree(
      { ...event.item, checked: !event.item.checked, oidPath: event.item.oidPath.slice(1), children: null },
      event.item.oidPath[0]
    );

  const onCheckChangeFlat = (event: { item: DimensionSearchTreeItem; itemHierarchicalIndex: string }) =>
    onChangeFromTree({ ...event.item, checked: !event.item.checked, oidPath: event.item.oidPath, children: null });

  const onCheckChange = hierarchical ? onCheckChangeHierarchical : onCheckChangeFlat;

  if (searchText?.length >= 3 && resultDimensions.length)
    return (
      <div
        style={{
          height: hierarchical ? "calc( 100vh - 13.5rem - 115px)" : "calc( 100vh - 13.5rem - 75px)",
          position: "relative",
          maxHeight: "inherit",
          border: "1px solid " + theme.palette.neutralLight,
          overflow: "auto",
        }}
      >
        <TreeView data={resultDimensions} expandIcons={false} item={SearchTreeViewItem({ onCheckChange })} />
      </div>
    );

  if (searchText?.length >= 3 && !resultDimensions.length && !bufferError) return <NoResultScreen />;
  if (searchText?.length >= 3 && bufferError) return <BufferErrorScreen />;
  return null;
};
